import {
  Box,
  Flex,
  FlexProps,
  FormLabel,
  FormLabelProps,
  Switch,
  SwitchProps,
} from "@chakra-ui/react";
import { useCallback } from "react";

function isTuple<FieldType>(
  value: FieldType | [FieldType, string]
): value is [FieldType, string] {
  return (
    Array.isArray(value) && value.length === 2 && typeof value[1] === "string"
  );
}

/**
 * @deprecated Consolidate with FormikSwitchInput
 */
export type SwitchInputProps<FieldType> = {
  name?: string;
  checked: FieldType | [FieldType, string];
  unchecked?: FieldType | [FieldType, string];
  label?: string;
  formLabelProps?: Omit<FormLabelProps, "flexDirection" | "flexDir">;
  containerProps?: Omit<FlexProps, "direction" | "dir">;
  labelDirection?: "row" | "column";
  switchDirection?: "row" | "column";
  onChange: (value: FieldType | undefined) => Promise<void> | void;
  value?: FieldType;
} & Omit<SwitchProps, "value" | "onChange">;
export function SwitchInput<FieldType>({
  name,
  checked,
  unchecked,
  label,
  formLabelProps,
  containerProps,
  value,
  id,
  onChange,
  switchDirection,
  ...props
}: SwitchInputProps<FieldType>) {
  const [checkedValue, checkedString] = isTuple(checked)
    ? checked
    : [checked, `${checked}`];
  const [uncheckedValue, uncheckedString] = isTuple(unchecked)
    ? unchecked
    : [unchecked, unchecked ? `${unchecked}` : undefined];

  const onChangeHandler = useCallback(() => {
    const nextValue = value === checkedValue ? uncheckedValue : checkedValue;
    onChange(nextValue);
  }, [checkedValue, onChange, uncheckedValue, value]);

  return (
    <FormLabel
      htmlFor={id ?? name}
      margin={!label ? 0 : undefined}
      gap={3}
      display="flex"
      flexDir={props.labelDirection === "row" ? "row-reverse" : "column"}
      alignSelf="flex-start"
      {...formLabelProps}
    >
      {label && <span>{label}</span>}
      <Flex
        direction={switchDirection ?? "column"}
        alignItems={switchDirection === "row" ? "center" : undefined}
        gap="2"
        {...containerProps}
      >
        <Switch
          {...props}
          id={id ?? name}
          onChange={onChangeHandler}
          isChecked={value === checkedValue}
        />
        <Box fontSize="sm">
          {value === checkedValue ? checkedString : uncheckedString}
        </Box>
      </Flex>
    </FormLabel>
  );
}
